import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Stack, Typography } from "@mui/material";
import { useEffect, useRef } from "react";

import { ScreenMobile } from "@/components/common/ScreenMobile";
import { PrimaryButton } from "@/components/common";
import {
  isReactNativeWebView,
  sendMessageToRN,
} from "@/utils/useWebViewBridge";

export interface MarketplacePaymentIframeOverlayProps {
  open: boolean;
  paymentUrl: string;
  storeName?: string;
  onBack: () => void;
}

const POPUP_FEATURES = "width=600,height=800,scrollbars=yes";

function isHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * Pagamento mobile via ScreenMobile.
 *
 * No fluxmedportable: envia a URL explícita ao nativo (OPEN_IN_APP_BROWSER).
 * Não depende de onOpenWindow/targetUrl (no Android costuma vir undefined →
 * “Domain: undefined” / SSL error falso).
 *
 * No browser: window.open (popup).
 */
export function MarketplacePaymentIframeOverlay({
  open,
  paymentUrl,
  storeName,
  onBack,
}: MarketplacePaymentIframeOverlayProps) {
  const openedOnceRef = useRef(false);
  const isRn = isReactNativeWebView();
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  const openPaymentPage = () => {
    if (!paymentUrl || !isHttpUrl(paymentUrl)) return;

    if (isRn) {
      // URL explícita → WebView interna com barra Voltar (evita Domain: undefined)
      sendMessageToRN({
        type: "OPEN_IN_APP_BROWSER",
        payload: {
          url: paymentUrl,
          title: storeName?.trim()
            ? `Pagamento · ${storeName.trim()}`
            : "Pagamento",
        },
      });
      return;
    }

    const popup = window.open(
      paymentUrl,
      `Pagamento-${Date.now()}`,
      POPUP_FEATURES,
    );
    if (!popup) {
      window.location.href = paymentUrl;
    }
  };

  useEffect(() => {
    if (!open || !paymentUrl) {
      openedOnceRef.current = false;
      return;
    }
    if (openedOnceRef.current) return;
    openedOnceRef.current = true;
    openPaymentPage();
  }, [open, paymentUrl]);

  useEffect(() => {
    if (!open || !isRn) return;

    const handleMessage = (event: MessageEvent) => {
      try {
        const raw = event.data;
        const data =
          typeof raw === "string" && raw.startsWith("{")
            ? JSON.parse(raw)
            : raw;
        if (data?.type === "IN_APP_BROWSER_CLOSED") {
          onBackRef.current();
        }
      } catch {
        // ignora
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [open, isRn]);

  return (
    <ScreenMobile
      open={open}
      onClose={onBack}
      title="Pagamento"
      subtitle={storeName}
      zIndexOffset={40}
    >
      <Stack
        spacing={2}
        sx={{
          p: 2,
          maxWidth: 420,
          mx: "auto",
          width: "100%",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {isRn
            ? "A página do dispensador abre neste app. Use ← Voltar na barra superior para retornar ao Fluxmed."
            : "Conclua o PIX ou cartão na página do dispensador. Quando terminar, feche com o X para voltar ao Fluxmed."}
        </Typography>
        <PrimaryButton
          width="100%"
          height="48px"
          onClick={openPaymentPage}
          startIcon={<OpenInNewIcon />}
        >
          Abrir pagamento
        </PrimaryButton>
        <Typography variant="caption" color="text.secondary">
          Se a página não abrir sozinha, use o botão acima.
        </Typography>
      </Stack>
    </ScreenMobile>
  );
}
